import type React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

interface Props {
    options: string[];
    selected: string[];
    placeholder: string;
    optionLabels?: Record<string, string>;
    onChange: (selected: string[]) => void;
}

const summaryLabel = (selected: string[], placeholder: string): string => {
    if (selected.length === 0) return placeholder;
    if (selected.length === 1) return selected[0];
    return `${selected.length} selected`;
};

const checkGlyph = (checked: boolean | 'indeterminate'): string => {
    if (checked === 'indeterminate') return '◐';
    return checked ? '✓' : '';
};

const checkboxItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 10px',
    fontSize: 12,
    color: 'var(--text)',
    borderRadius: 3,
    cursor: 'pointer',
    outline: 'none',
    userSelect: 'none',
    whiteSpace: 'nowrap',
};

const checkboxBoxStyle = (checked: boolean | 'indeterminate'): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 14,
    height: 14,
    flexShrink: 0,
    fontSize: 10,
    lineHeight: 1,
    border: '1px solid var(--border)',
    borderRadius: 3,
    backgroundColor: checked ? 'var(--primary, #818cf8)' : 'var(--bg-raised)',
    color: '#fff',
});

/**
 * Checkbox-style multiselect filter built on Radix's DropdownMenu: any number of `options`
 * can be selected at once, ORed together (row matches if its value is any one of the
 * selected options) -- see filterRows' 'multiselect' branch. A synthetic "Select All" item
 * toggles every option on/off.
 *
 * Replaces an earlier react-select implementation -- filter cells live inside
 * .table-scroll-container (overflow-x/y: auto for wide-table scrolling), and react-select's
 * menu (even portaled with a fixed position) could still end up clipped or misaligned once
 * scrolled. Radix's Content is positioned via Floating UI with collision detection built in
 * (`avoidCollisions`, default on), so it dynamically flips/shifts to stay in the viewport
 * instead of relying on a static position guess.
 */
export const MultiSelectFilter = ({
    options,
    selected,
    placeholder,
    optionLabels,
    onChange,
}: Props) => {
    const allSelected = options.length > 0 && options.every((o) => selected.includes(o));
    const someSelected = options.some((o) => selected.includes(o));
    const selectAllChecked: boolean | 'indeterminate' = allSelected
        ? true
        : someSelected
          ? 'indeterminate'
          : false;

    const toggleOption = (option: string, checked: boolean): void => {
        onChange(checked ? [...selected, option] : selected.filter((s) => s !== option));
    };

    const toggleSelectAll = (): void => {
        onChange(allSelected ? [] : [...options]);
    };

    return (
        <DropdownMenu.Root modal={false}>
            <DropdownMenu.Trigger asChild>
                <button
                    type="button"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 6,
                        width: '100%',
                        minHeight: 30,
                        fontSize: 12,
                        padding: '4px 8px',
                        backgroundColor: 'var(--bg-raised)',
                        border: '1px solid var(--border)',
                        borderRadius: 4,
                        color: selected.length > 0 ? 'var(--text)' : 'var(--muted)',
                        cursor: 'pointer',
                    }}
                >
                    <span
                        style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {summaryLabel(selected, placeholder)}
                    </span>
                    <span aria-hidden style={{ opacity: 0.6, fontSize: 10 }}>
                        ▾
                    </span>
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="start"
                    sideOffset={4}
                    collisionPadding={8}
                    style={{
                        backgroundColor: 'var(--card)',
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        padding: 4,
                        minWidth: 200,
                        maxHeight: 320,
                        overflowY: 'auto',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                        zIndex: 9999,
                    }}
                >
                    <DropdownMenu.CheckboxItem
                        checked={selectAllChecked}
                        onSelect={(e) => e.preventDefault()}
                        onCheckedChange={toggleSelectAll}
                        style={checkboxItemStyle}
                    >
                        <span aria-hidden style={checkboxBoxStyle(selectAllChecked)}>
                            {checkGlyph(selectAllChecked)}
                        </span>
                        Select All
                    </DropdownMenu.CheckboxItem>
                    <DropdownMenu.Separator
                        style={{ height: 1, backgroundColor: 'var(--border)', margin: '4px 0' }}
                    />
                    {options.map((option) => {
                        const checked = selected.includes(option);
                        return (
                            <DropdownMenu.CheckboxItem
                                key={option}
                                checked={checked}
                                onSelect={(e) => e.preventDefault()}
                                onCheckedChange={(next) => toggleOption(option, next)}
                                style={checkboxItemStyle}
                            >
                                <span aria-hidden style={checkboxBoxStyle(checked)}>
                                    {checkGlyph(checked)}
                                </span>
                                {optionLabels?.[option] ?? option}
                            </DropdownMenu.CheckboxItem>
                        );
                    })}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
};
